import { Box, For, Table } from '@chakra-ui/react';
import { useState } from 'react';
import { ChevronDownIcon } from '@/assets/icons';
import ActionMenuIcon from '@/assets/icons/ActionMenuIcon';
import FaceliftCheckbox from '@/components/Atoms/FaceliftCheckbox';
import Show from '@/components/Molecules/Show';
import type { DataTableRowProps } from '@/components/Organisms/DataTable/DataTable.types';
import DataTableActioMenu from '@/components/Organisms/DataTable/DataTableActionMenu';
import { colors } from '@/components/ui/theme';

const DataTableRow = ({
	item,
	columns,
	isSelectable,
	selection,
	bordered,
	setSelection,
	actions = [],
	size = 3,
	allowExpand = true,
	selectColumnRef,
	onSelectColumnRefReady,
	actionMenuOpenOverride = false,
}: DataTableRowProps) => {
	const [isExpanded, setIsExpanded] = useState(false);

	// Callback ref to notify parent when element is mounted
	const handleCellRef = (element: HTMLTableCellElement | null) => {
		if (element && selectColumnRef && 'current' in selectColumnRef) {
			(selectColumnRef as React.MutableRefObject<HTMLElement | null>).current = element;
		}
		if (onSelectColumnRefReady) {
			onSelectColumnRefReady(element);
		}
	};

	const handleExpand = () => {
		setIsExpanded(!isExpanded);
	};

	return (
		<>
			<Table.Row
				key={item.id}
				data-selected={selection.includes(String(item.id)) ? '' : undefined}
				bg={{
					base: bordered ? '' : '{colors.gray.50}',
					_dark: colors.tabelCellDark,
				}}
			>
				<Show when={!!isSelectable}>
					<Table.Cell
						ref={handleCellRef}
						w="100px"
						borderBottom="1px solid"
						height="2.5rem"
						p={size}
						borderColor={{
							base: bordered ? '{colors.gray.200}' : '{colors.gray.50}',
							_dark: bordered ? '{colors.gray.600}' : colors.tabelCellDark,
						}}
						transition="all 0.3s ease-in-out"
					>
						<FaceliftCheckbox
							size="xs"
							mt={1}
							aria-label="Select row"
							checked={selection.includes(String(item.id))}
							borderColor="gray.900"
							_checked={{
								'& .chakra-checkbox__control': {
									background: 'brand.solid',
									borderColor: 'brand.solid',
									color: 'gray.200',
									borderRadius: 0,
								},
							}}
							onCheckedChange={(checked: boolean) => {
								setSelection((prev) =>
									checked
										? [...prev, String(item.id)]
										: prev.filter((id) => id !== String(item.id)),
								);
							}}
						/>
					</Table.Cell>
				</Show>
				<Show when={allowExpand}>
					<Table.Cell
						w="20px"
						borderBottom="1px solid"
						height="2.5rem"
						cursor="pointer"
						py={size}
						px={0}
						borderColor={{
							base: bordered ? '{colors.gray.200}' : '{colors.gray.50}',
							_dark: bordered ? '{colors.gray.600}' : colors.tabelCellDark,
						}}
						transition="all 0.3s ease-in-out"
						onClick={handleExpand}
					>
						<Show when={!!item.subRows && item.subRows.length > 0}>
							<Box
								display="flex"
								justifyContent="center"
								transitionProperty='transform'
								transitionDuration='moderate'
								transform={`rotate(${isExpanded ? -180 : 0}deg)`}
							>
								<ChevronDownIcon width={12} height={12} />
							</Box>
						</Show>
					</Table.Cell>
				</Show>
				<For each={columns}>
					{(col) => (
						<Table.Cell
							w={col.width || '100px'}
							key={col.key}
							borderBottom="1px solid"
							height="2.5rem"
							p={size}
							borderColor={{
								base: bordered ? '{colors.gray.200}' : '{colors.gray.50}',
								_dark: bordered ? '{colors.gray.600}' : colors.tabelCellDark,
							}}
							fontSize="sm"
							transition="all 0.3s ease-in-out"
						>
							{item[col.key] as React.ReactNode}
						</Table.Cell>
					)}
				</For>
				<Show when={actions.length > 0}>
					<Table.Cell
						border={0}
						fontSize="sm"
						fontWeight={600}
						height="2.5rem"
						w="100px"
						borderBottom="1px solid"
						borderColor={{
							base: bordered ? '{colors.gray.200}' : '{colors.gray.50}',
							_dark: bordered ? '{colors.gray.600}' : colors.tabelCellDark,
						}}
						p={size}
						transition="all 0.3s ease-in-out"
					>
					<DataTableActioMenu
						item={item}
						actions={actions}
						openOverride={actionMenuOpenOverride}
						trigger={
							<Box
								display="flex"
								justifyContent="center"
								cursor="pointer"
								ml={4}
							>
								<ActionMenuIcon />
							</Box>
						}
					/>
					</Table.Cell>
				</Show>
			</Table.Row >
			<Show when={!!item.subRows && item.subRows.length > 0 && isExpanded}>
				<For each={item.subRows}>
					{(subRow, index) => (
						<Table.Row
							key={subRow.id}
							bg={{
								base: bordered ? '' : '{colors.gray.50}',
								_dark: colors.tabelCellDark,
							}}
						>
							<Show when={!!isSelectable}>
								<Table.Cell
									border={0}
									w="100px"
									borderBottom={item.subRows && index === item.subRows.length - 1 ? "1px solid" : "0px"}
									height="2.5rem"
									p={size}
									borderColor={{
										base: bordered ? '{colors.gray.200}' : '{colors.gray.50}',
										_dark: bordered
											? '{colors.gray.600}'
											: colors.tabelCellDark,
									}}
									transition="all 0.3s ease-in-out"
								/>
							</Show>
							<Table.Cell
								border={item.subRows && index === item.subRows.length - 1 ? 1 : 0}
								w="20px"
								borderBottom={item.subRows && index === item.subRows.length - 1 ? "1px solid" : "0px"}
								height="2.5rem"
								p={size}
								borderColor={{
									base: bordered ? '{colors.gray.200}' : '{colors.gray.50}',
									_dark: bordered ? '{colors.gray.600}' : colors.tabelCellDark,
								}}
								transition="all 0.3s ease-in-out"
							/>
							<For each={columns}>
								{(col) => (
									<Table.Cell
										border={item.subRows && index === item.subRows.length - 1 ? 1 : 0}
										w={col.width || '100px'}
										key={col.key}
										borderBottom={item.subRows && index === item.subRows.length - 1 ? "1px solid" : "0px"}
										height="2.5rem"
										p={size}
										borderColor={{
											base: bordered ? '{colors.gray.200}' : '{colors.gray.50}',
											_dark: bordered
												? '{colors.gray.600}'
												: colors.tabelCellDark,
										}}
										fontSize="sm"
										transition="all 0.3s ease-in-out"
									>
										{subRow[col.key] as React.ReactNode}
									</Table.Cell>
								)}
							</For>
							<Show when={actions.length > 0}>
								<Table.Cell
									border={item.subRows && index === item.subRows.length - 1 ? 1 : 0}
									fontSize="sm"
									fontWeight={600}
									height="2.5rem"
									w="100px"
									borderBottom={item.subRows && index === item.subRows.length - 1 ? "1px solid" : "0px"}
									borderColor={{
										base: bordered ? '{colors.gray.200}' : '{colors.gray.50}',
										_dark: bordered
											? '{colors.gray.600}'
											: colors.tabelCellDark,
									}}
									p={size}
									transition="all 0.3s ease-in-out"
								>
									{subRow.action as React.ReactNode}
								</Table.Cell>
							</Show>
						</Table.Row>
					)}
				</For>
			</Show>
		</>
	);
};

export default DataTableRow;
